import { motion, useReducedMotion } from 'framer-motion';
import { useSite } from '../content';

const easeOut = [0.22, 1, 0.36, 1] as const;

export function About() {
  const { about } = useSite();
  const prefersReducedMotion = useReducedMotion();

  if (!about.heading && !about.intro && !about.body) return null;

  const fadeUp = {
    initial: prefersReducedMotion ? false : { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-100px' },
  } as const;

  return (
    <section
      id="about"
      aria-labelledby="about-heading"
      className="relative w-full bg-slate-dark pt-20 md:pt-24 lg:pt-28 pb-32 md:pb-44 lg:pb-52"
    >
      <div
        aria-hidden="true"
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[88%] max-w-6xl h-px bg-textlight/15"
      />

      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <motion.h2
          id="about-heading"
          {...fadeUp}
          transition={{ duration: prefersReducedMotion ? 0 : 0.8, delay: 0.05, ease: easeOut }}
          className="font-serif font-light text-textlight text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
        >
          {about.heading}
        </motion.h2>

        <motion.div
          aria-hidden="true"
          initial={prefersReducedMotion ? false : { scaleX: 0, opacity: 0 }}
          whileInView={{ scaleX: 1, opacity: 1 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: prefersReducedMotion ? 0 : 1, delay: 0.2, ease: easeOut }}
          style={{ transformOrigin: 'left' }}
          className="mt-14 md:mt-20 h-px w-24 bg-bronze/60"
        />

        <div className="mt-14 md:mt-20 space-y-12 md:space-y-16">
          {about.intro ? (
            <motion.p
              {...fadeUp}
              transition={{ duration: prefersReducedMotion ? 0 : 0.8, delay: 0.15, ease: easeOut }}
              className="text-textlight/80 text-lg md:text-xl leading-[1.7] font-light"
            >
              {about.intro}
            </motion.p>
          ) : null}

          {about.body ? (
            <motion.p
              {...fadeUp}
              transition={{ duration: prefersReducedMotion ? 0 : 0.8, delay: 0.25, ease: easeOut }}
              className="text-textlight/75 text-lg md:text-xl leading-[1.75] font-light"
            >
              {about.body}
            </motion.p>
          ) : null}

          {about.closingPrefix || about.closingHighlight ? (
            <motion.p
              {...fadeUp}
              transition={{ duration: prefersReducedMotion ? 0 : 0.9, delay: 0.35, ease: easeOut }}
              className="pt-6 md:pt-10 font-serif font-light italic text-textlight text-2xl md:text-3xl lg:text-4xl leading-[1.4] tracking-tight"
            >
              {about.closingPrefix}
              {about.closingPrefix && about.closingHighlight ? ' ' : ''}
              {about.closingHighlight ? (
                <span className="not-italic font-normal text-bronze-light">
                  {about.closingHighlight}
                </span>
              ) : null}
            </motion.p>
          ) : null}
        </div>
      </div>

      <div
        aria-hidden="true"
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[88%] max-w-6xl h-px bg-textlight/15"
      />
    </section>
  );
}
